import React, { FC, useEffect } from 'react';

import { PolicyResponse } from '@ariksa/abac/api';
import { CloudProviders } from '@ariksa/cloud-account/api';
import { NativeResources } from '@ariksa/inventory-core';
import { Box, Center, Stack, Wrap, WrapItem } from '@chakra-ui/react';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  EllipsisText,
  formatTooltip,
  getIconColor,
  renderIconWithLabel,
  StackedCell,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { IconTypes, MonitoringIcon } from 'components/Icons';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { seletMaskingPolicies } from 'containers/Jit/MaskingPolicies/selectors';
import { actions } from 'containers/Jit/MaskingPolicies/slice';

interface Props {
  row: PolicyResponse;
}

export const DataPolicyRoles: FC<Props> = props => {
  const { row } = props;
  const [searchTerm, setSearchTerm] = React.useState('');
  const [finalSearchTerm, setFinalSearchTerm] = React.useState('');
  const { dataPolicy } = useSelector(seletMaskingPolicies);
  const dispatch = useDispatch();
  const { getCloudNativeName } = useResourceType();
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();

  const openSnowflakeRole = (role: Record<string, any>) => {
    updateActiveResource({
      resourceType: NativeResources.SnowflakeRoles,
      uuid: role?.role_uuid ?? role?.uuid ?? role?.resource_uuid,
      resourceId: role?.resource_id ?? role?.role_uuid,
      accountId: role?.account_id,
      cloudProvider: CloudProviders.Snowflake,
    });
    onOpenMetadata();
  };

  useEffect(() => {
    dispatch(
      actions.getPolicyById({
        q: { policyId: row.policy_id },
      }),
    );
  }, [dispatch, row]);

  const columns = [
    {
      header: <Box pl={10}>Role Name</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.Role}
          color={getIconColor(row.provider)}
          useCustomColor
        >
          <StackedCell
            upper={row?.role_name}
            lower={getCloudNativeName(
              row.native_class ?? NativeResources.SnowflakeRoles,
            )}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Account',
      render: ({ row }) =>
        renderIconWithLabel(IconTypes.Snowflake, row.account_name),
      align: 'left',
    },
    {
      header: 'Data Groups',
      align: 'left',
      render: ({ row }) => {
        const dataGroups = row?.data_groups;
        const dataGroupNames = map(dataGroups, dataGroup => dataGroup?.name);

        return !!dataGroups?.length
          ? renderIconWithLabel(
              IconTypes.DataGroup,
              <EllipsisText
                label={dataGroupNames.join(', ')}
                tooltip={formatTooltip({
                  header: 'Data Groups',
                  content: (
                    <Stack>
                      {map(dataGroupNames, (dataGroup, index) => (
                        <Box key={`${dataGroup}-${index}`}>
                          {index + 1}. {dataGroup}
                        </Box>
                      ))}
                    </Stack>
                  ),
                  width: 48,
                })}
              />,
              customTheme.colors.blue['300'],
            )
          : '-';
      },
    },
    {
      header: 'Masking Policy',
      accessor: 'masking_type',
      align: 'left',
    },
    {
      header: 'Permissions',
      accessor: 'permissions',
      align: 'left',
      render: ({ value }) => (
        <Wrap spacing={1}>
          {map(value, o => (
            <WrapItem>
              <Tag
                label={o?.toUpperCase()}
                styles={{
                  label: { fontSize: 'sm', color: 'primary' },
                  tag: {
                    borderColor: customTheme.colors.gray['100'],
                    bg: 'white',
                    py: 0.5,
                    px: 1,
                  },
                }}
              />
            </WrapItem>
          ))}
        </Wrap>
      ),
    },
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center onClick={e => e.stopPropagation()}>
          <ActionButton
            label="View Snowflake role"
            icon={<MonitoringIcon />}
            onClick={() => openSnowflakeRole(row)}
          />
        </Center>
      ),
    },
  ];

  return (
    <Stack h="full" spacing={5}>
      {/*<BorderedBox>
        <HStack>
          <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
          <Box w={64}>
            <SearchBox
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search"
            />
          </Box>
        </HStack>
      </BorderedBox>*/}
      <Box flex={1} h="full" w="full">
        <Table
          columns={columns}
          data={dataPolicy.data?.roles ?? []}
          isLoading={dataPolicy.isLoading}
        />
      </Box>
    </Stack>
  );
};
